import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import { ToastStatusEnum } from '@components/ui/toaster';
import { selectActiveUser } from '@redux/authent/authent.selectors';
import { usePostFlirCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';

import { FlirCommand, FlirCommandEnum } from '@/types/c2/c2.types';
import { MonitoringSensorByType } from '@/types/sensor/monitoring.types';
import { FlirSensorConfiguration, SensorTypeEnum } from '@/types/sensor/sensors';

import { useAppSelector } from './redux.hooks';

type UseFlirCommandParameter =
  | MonitoringSensorByType<SensorTypeEnum.FLIR>
  | SensorOrSubSensorInfoByType<SensorTypeEnum.FLIR>
  | null;

export type UseFlirCommandFunctions = {
  setEmission: (emitting: boolean, configuration?: FlirSensorConfiguration) => void;
  isLoading: boolean;
  isError: boolean;
};

export const useFlirCommand = (monitoringSensor: Readonly<UseFlirCommandParameter>) => {
  const [postFlirCommand, { isError, isLoading }] = usePostFlirCommandMutation();

  const user = useAppSelector(selectActiveUser);
  const sensorConfiguration = monitoringSensor?.configuration ?? null;
  const site = user.activeSite?.code ?? '';
  const appCode = sensorConfiguration?.appCode ?? '';
  const code = sensorConfiguration?.code ?? '';
  const name = sensorConfiguration?.name ?? '';

  const getCommand = (command: FlirCommand) => {
    switch (command.type) {
      case FlirCommandEnum.CALIBRATE: {
        return { command: 'sensors.commands.flir.calibrate' };
      }
      case FlirCommandEnum.CALIBRATE_AZ: {
        return { command: 'sensors.commands.flir.calibrate' };
      }
      case FlirCommandEnum.CALIBRATE_POS: {
        return { command: 'sensors.commands.flir.calibrate' };
      }
      case FlirCommandEnum.RESET: {
        return { command: 'sensors.commands.flir.reset' };
      }
      case FlirCommandEnum.STBY: {
        return { command: 'sensors.commands.flir.emissionCommand', commandValues: { state: false } };
      }
      case FlirCommandEnum.XMIT: {
        return { command: 'sensors.commands.flir.emissionCommand', commandValues: { state: true } };
      }
    }
  };

  async function sendCommand(command: FlirCommand, configuration?: FlirSensorConfiguration) {
    const fullCommand = {
      site: configuration?.site ?? site,
      appCode: configuration?.appCode ?? appCode,
      command,
    };

    const toastParams = {
      ...getCommand(command),
      name: configuration?.name ?? name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    return postFlirCommand(fullCommand)
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.ERROR,
        }),
      );
  }

  const setEmission = (emitting: boolean, configuration?: FlirSensorConfiguration) => {
    sendCommand(
      {
        type: emitting ? FlirCommandEnum.XMIT : FlirCommandEnum.STBY,
        sensorCode: configuration?.code ?? code,
      } as FlirCommand,
      configuration,
    );
  };

  return {
    setEmission,
    isLoading,
    isError,
  } as UseFlirCommandFunctions;
};
